import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  LayoutDashboard, 
  Users, 
  BookOpen, 
  FileText, 
  GraduationCap, 
  Settings, 
  ChevronLeft, 
  ChevronRight,
  LogOut,
  X,
  Sliders,
  ListChecks,
  Type,
  UserCog,
  Palette,
  Circle,
  Key
} from 'lucide-react';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AdminSidebar = ({ collapsed, setCollapsed, mobileOpen, setMobileOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [systemOnline, setSystemOnline] = useState(null);

  // Fetch system status
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await axios.get(`${API}/api/public/system-status`);
        setSystemOnline(response.data.online);
      } catch (error) {
        setSystemOnline(false);
      }
    };
    fetchStatus();
    // Poll every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const menuItems = [
    {
      title: 'Übersicht',
      icon: LayoutDashboard,
      path: '/admin/dashboard',
      key: 'dashboard'
    },
    {
      title: 'Teilnehmer',
      icon: Users,
      path: '/admin/teilnehmer',
      key: 'teilnehmer'
    },
    {
      title: 'Kursverwaltung',
      icon: BookOpen,
      key: 'kursverwaltung',
      children: [
        {
          title: 'Kursdetails',
          icon: GraduationCap,
          path: '/admin/kursdetails',
          key: 'kursdetails'
        },
        {
          title: 'Textbausteine',
          icon: FileText,
          path: '/admin/textbausteine',
          key: 'textbausteine'
        }
      ]
    },
    {
      title: 'Einstellungen',
      icon: Settings,
      key: 'einstellungen',
      children: [
        {
          title: 'System',
          icon: Sliders,
          path: '/admin/einstellungen/system',
          key: 'system'
        },
        {
          title: 'Feldmapping',
          icon: ListChecks,
          path: '/admin/einstellungen/feldmapping',
          key: 'feldmapping'
        },
        {
          title: 'Texte',
          icon: Type,
          path: '/admin/einstellungen/texte',
          key: 'texte'
        },
        {
          title: 'Kursfarben',
          icon: Palette,
          path: '/admin/einstellungen/kursfarben',
          key: 'kursfarben'
        },
        {
          title: 'Benutzer',
          icon: UserCog,
          path: '/admin/einstellungen/benutzer',
          key: 'benutzer'
        },
        {
          title: 'Passwort ändern',
          icon: Key,
          path: '/admin/einstellungen/passwort',
          key: 'passwort'
        }
      ]
    }
  ];

  const [expandedItems, setExpandedItems] = useState(['kursverwaltung', 'einstellungen']);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  const toggleExpand = (key) => {
    setExpandedItems(prev => 
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const isActive = (path) => location.pathname === path;
  
  const isParentActive = (item) => {
    if (!item.children) return false;
    return item.children.some(child => location.pathname === child.path);
  };

  const renderMenuItem = (item) => {
    if (item.children) {
      const isExpanded = expandedItems.includes(item.key);
      const hasActiveChild = isParentActive(item);
      
      return (
        <div key={item.key} className="mb-1">
          <button
            onClick={() => toggleExpand(item.key)}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
              hasActiveChild 
                ? 'bg-blue-50 text-blue-700' 
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center gap-3">
              <item.icon className="h-5 w-5 flex-shrink-0" />
              {!collapsed && <span className="font-medium">{item.title}</span>}
            </div>
            {!collapsed && (
              <ChevronRight className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
            )}
          </button>
          {isExpanded && !collapsed && (
            <div className="ml-6 mt-1 space-y-1 border-l-2 border-gray-200 pl-2">
              {item.children.map(child => (
                <button
                  key={child.key}
                  onClick={() => {
                    navigate(child.path);
                    setMobileOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    isActive(child.path)
                      ? 'bg-blue-100 text-blue-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <child.icon className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm">{child.title}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <button
        key={item.key}
        onClick={() => {
          navigate(item.path);
          setMobileOpen(false);
        }}
        className={`w-full flex items-center gap-3 px-3 py-2 mb-1 rounded-lg transition-colors ${
          isActive(item.path)
            ? 'bg-blue-100 text-blue-700 font-medium'
            : 'text-gray-700 hover:bg-gray-100'
        }`}
      >
        <item.icon className="h-5 w-5 flex-shrink-0" />
        {!collapsed && <span>{item.title}</span>}
      </button>
    );
  };

  const sidebarContent = (
    <div className="h-full flex flex-col bg-white border-r border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <span className="font-bold text-gray-900">MSY Admin</span>
          </div>
        )}
        {collapsed && (
          <span className="font-bold text-gray-900 mx-auto">MSY</span>
        )}
        {/* Mobile Close Button */}
        <button 
          onClick={() => setMobileOpen(false)}
          className="lg:hidden"
        >
          <X className="h-6 w-6 text-gray-600" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 overflow-y-auto">
        {menuItems.map(renderMenuItem)}
      </nav>

      {/* Footer with Status */}
      <div className="p-4 border-t border-gray-200">
        {/* System Status */}
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg mb-2 ${
          systemOnline === null ? 'bg-gray-100' : systemOnline ? 'bg-green-50' : 'bg-red-50'
        }`}>
          <Circle 
            className={`h-3 w-3 ${
              systemOnline === null ? 'text-gray-400' : systemOnline ? 'text-green-500 fill-green-500' : 'text-red-500 fill-red-500'
            }`} 
          />
          {!collapsed && (
            <span className={`text-sm font-medium ${
              systemOnline === null ? 'text-gray-500' : systemOnline ? 'text-green-700' : 'text-red-700'
            }`}>
              {systemOnline === null ? 'Lade...' : systemOnline ? 'System Online' : 'System Offline'}
            </span>
          )}
        </div>

        <Button
          onClick={handleLogout}
          variant="ghost"
          className="w-full justify-start text-gray-700 hover:bg-red-50 hover:text-red-600"
        >
          <LogOut className="h-5 w-5 mr-3" />
          {!collapsed && <span>Abmelden</span>}
        </Button>
        
        {/* Desktop Collapse Toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex w-full items-center justify-center mt-2 py-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
        >
          {collapsed ? (
            <ChevronRight className="h-5 w-5" />
          ) : (
            <>
              <ChevronLeft className="h-5 w-5 mr-2" />
              <span className="text-sm">Einklappen</span>
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <div 
        className={`hidden lg:block h-screen sticky top-0 transition-all duration-300 ${
          collapsed ? 'w-20' : 'w-64'
        }`}
      >
        {sidebarContent}
      </div>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <div 
        className={`lg:hidden fixed top-0 left-0 h-screen w-64 z-50 transform transition-transform duration-300 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {sidebarContent}
      </div>
    </>
  );
};

export default AdminSidebar;
